import {
  mdBumps,
  Section,
  SectionContent,
  SectionDescription,
  SectionProps,
  SectionTitle,
} from '@wrisk/ui-components'
import { groupBy } from 'lodash-es'
import React, { FunctionComponent, useMemo, useState } from 'react'

import {
  CoverLevelOptionSelector,
  getActiveCoverLevelOption,
  isMatchingCoverLevelOption,
} from '../../../../domain'
import {
  TKey,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { useUpdateEffect } from '../../../../util/useUpdateEffect'
import { useProposal } from '../../../product/proposal/ProposalProvider'
import { CoverLevelSelection } from './CoverLevelSelection'
import { PaymentScheduleSelection } from './PaymentScheduleSelection'

const tKey = TKey('components.proposal-configuration-section')

export const ProposalConfigurationSection: FunctionComponent<SectionProps> = (props) => {
  const { t } = useWriskTranslation()
  const {
    dispatch,
    proposal: { quote },
  } = useProposal()
  const subheader = t(tKey('subheader'), { defaultValue: null })

  const [selection, setSelection] = useState<CoverLevelOptionSelector>(() => {
    const coverOption = getActiveCoverLevelOption(quote)

    return {
      coverLevel: coverOption?.coverLevel,
      paymentScheduleType: coverOption?.paymentScheduleType,
    }
  })

  const coverLevelOptions = useMemo(
    () => groupBy(quote.availableCoverLevelOptions, (option) => option.coverLevel),
    [quote],
  )

  useUpdateEffect(() => {
    dispatch({
      type: 'SELECT_COVER_OPTION',
      payload: selection,
    })
  }, [selection])

  const coverLevels = Object.keys(coverLevelOptions)
  const coverOptions = coverLevelOptions[selection.coverLevel ?? ''] ?? []

  return quote.availableCoverLevelOptions.length > 1 ? (
    <Section width={1} {...props}>
      <SectionTitle>{t(tKey('header'))}</SectionTitle>
      {subheader && <SectionDescription>{subheader}</SectionDescription>}
      <SectionContent
        display='flex'
        flexDirection='column'
        rowGap={mdBumps}
        p={mdBumps}
        mt={mdBumps}
        variant='raised'
        width={1}
      >
        <CoverLevelSelection
          tKey={tKey}
          coverLevels={coverLevels}
          value={selection.coverLevel}
          onChange={(coverLevel) =>
            setSelection((prev) => {
              const newOptions = coverLevelOptions[coverLevel]
              const newMatch = newOptions?.find(
                isMatchingCoverLevelOption({
                  coverLevel: coverLevel,
                  paymentScheduleType: prev.paymentScheduleType,
                }),
              )

              return {
                coverLevel,
                paymentScheduleType:
                  newMatch?.paymentScheduleType ??
                  newOptions?.[0].paymentScheduleType ??
                  prev.paymentScheduleType,
              }
            })
          }
        />
        <PaymentScheduleSelection
          tKey={tKey}
          coverOptions={coverOptions}
          value={selection.paymentScheduleType}
          onChange={(value) =>
            setSelection((prev) => ({
              ...prev,
              paymentScheduleType: value,
            }))
          }
        />
      </SectionContent>
    </Section>
  ) : null
}
